const formatDate = Intl.DateTimeFormat('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' });
const formatTime = Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit' });
const formatPast = Intl.DateTimeFormat('en-US', { weekday: 'short', month: 'short', day: 'numeric' });

const normalizeDate = (input: number | Date | string): Date | undefined => {
  let timestamp: number;
  if (typeof input === 'number') {
    timestamp = input;
  } else if (typeof input === 'string') {
    timestamp = new Date(input).getTime();
    if (isNaN(timestamp)) {
      return undefined;
    }
  } else {
    timestamp = input.getTime();
  }

  return new Date(timestamp);
};

export const formatRelativeTimestamp = (input: number | Date | string): string => {
  const time = normalizeDate(input);
  if (!time) {
    return '';
  }

  const timeFormattedDate = formatDate.format(time);

  const now = new Date();
  const nowFormattedDate = formatDate.format(now);

  if (nowFormattedDate === timeFormattedDate) {
    return `Today ${formatTime.format(time)}`;
  }

  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  const yesterdayFormattedDate = formatDate.format(yesterday);

  if (yesterdayFormattedDate === timeFormattedDate) {
    return `Yesterday ${formatTime.format(time)}`;
  }

  return formatPast.format(time);
};

export const timestampIsToday = (timestamp: number): boolean => {
  const now = new Date();
  const time = new Date(timestamp);

  return (
    now.getFullYear() === time.getFullYear() && now.getMonth() === time.getMonth() && now.getDate() === time.getDate()
  );
};

const isWithinDays = (timestamp: number, days: number): boolean => {
  const now = new Date();
  const time = new Date(timestamp);

  const startOfToday = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
  const startOfTime = Date.UTC(time.getFullYear(), time.getMonth(), time.getDate());

  const daysDiff = (startOfToday - startOfTime) / (1000 * 60 * 60 * 24);

  return daysDiff >= 0 && daysDiff < days;
};

export const timestampIsThisWeek = (timestamp: number): boolean => {
  return isWithinDays(timestamp, 7);
};

export const timestampIsThisMonth = (timestamp: number): boolean => {
  return isWithinDays(timestamp, 30);
};

const formatWeekday = Intl.DateTimeFormat('en-US', { weekday: 'long' });
const formatWithinYear = Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' });
const formatFarPast = Intl.DateTimeFormat('en-US', { year: '2-digit', month: '2-digit', day: '2-digit' });

const ONE_WEEK = 1000 * 60 * 60 * 24 * 7;

export const formatShortRelativeTimestamp = (input?: number | Date | string | null): string => {
  if (!input) {
    return '';
  }

  const time = normalizeDate(input);
  if (!time) {
    return '';
  }

  const now = new Date();
  if (now.getFullYear() !== time.getFullYear()) {
    return formatFarPast.format(time);
  }

  if (now.getTime() - time.getTime() > ONE_WEEK) {
    return formatWithinYear.format(time);
  }

  if (now.getDate() !== time.getDate()) {
    return formatWeekday.format(time);
  }

  return formatTime.format(time);
};
